// ===========================================
// Edit Meet & Greet Details Dialog (admin)
// ===========================================
//
// A parent typed the wrong phone or email. Edits the parent-entered fields
// only — contact 1, contact 2, emergency contact and the student's name.
// Status, teacher, schedule and notes have their own paths and are not here.
//
// Sends a PARTIAL patch: only the fields that actually differ from the
// booking, so an untouched field can never be rewritten by a stale form.
//
// ⚠️ Changing the primary EMAIL on a booking that holds a live registration
// link REVOKES that link server-side (it is an account-creation + payment
// credential sitting in an inbox the admin has just declared wrong). Since
// #249 the same save sends a fresh link to the corrected address when the
// server says the booking may be re-sent one (`canResendRegistration`), and a
// booking awaiting email verification gets a fresh verification email. The
// dialog says which will happen before the admin commits.

import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import { useUpdateMeetAndGreetDetails } from '../../hooks/useMeetAndGreet';
import type { MeetAndGreet, UpdateMeetAndGreetDetailsInput } from '../../api/meetAndGreet.api';

const RELATIONSHIPS = ['Mother', 'Father', 'Guardian', 'Grandparent', 'Other'];
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type FormState = {
  studentFirstName: string;
  studentLastName: string;
  contact1Name: string;
  contact1Email: string;
  contact1Phone: string;
  contact1Relationship: string;
  contact2Name: string;
  contact2Email: string;
  contact2Phone: string;
  contact2Relationship: string;
  emergencyName: string;
  emergencyPhone: string;
  emergencyRelationship: string;
};

const FIELDS = [
  'studentFirstName', 'studentLastName',
  'contact1Name', 'contact1Email', 'contact1Phone', 'contact1Relationship',
  'contact2Name', 'contact2Email', 'contact2Phone', 'contact2Relationship',
  'emergencyName', 'emergencyPhone', 'emergencyRelationship',
] as const;

function fromBooking(b: MeetAndGreet | null): FormState {
  const s = (v: string | null | undefined) => v ?? '';
  return {
    studentFirstName: s(b?.studentFirstName),
    studentLastName: s(b?.studentLastName),
    contact1Name: s(b?.contact1Name),
    contact1Email: s(b?.contact1Email),
    contact1Phone: s(b?.contact1Phone),
    contact1Relationship: s(b?.contact1Relationship),
    contact2Name: s(b?.contact2Name),
    contact2Email: s(b?.contact2Email),
    contact2Phone: s(b?.contact2Phone),
    contact2Relationship: s(b?.contact2Relationship),
    emergencyName: s(b?.emergencyName),
    emergencyPhone: s(b?.emergencyPhone),
    emergencyRelationship: s(b?.emergencyRelationship),
  };
}

/**
 * The partial patch: every field whose TRIMMED value differs from the booking.
 * Exported so the diffing (the property that keeps stale fields safe) is
 * testable on its own.
 */
export function buildDetailsPatch(booking: MeetAndGreet, form: FormState): UpdateMeetAndGreetDetailsInput {
  const original = fromBooking(booking);
  const patch: Record<string, string> = {};
  for (const key of FIELDS) {
    const next = form[key].trim();
    if (next !== original[key].trim()) patch[key] = next;
  }
  return patch as UpdateMeetAndGreetDetailsInput;
}

export interface EditMeetAndGreetDetailsDialogProps {
  open: boolean;
  booking: MeetAndGreet | null;
  onClose: () => void;
  /** Called with the server's updated row so the caller can refresh what it is showing. */
  onSaved?: (updated: MeetAndGreet) => void;
}

export default function EditMeetAndGreetDetailsDialog({
  open,
  booking,
  onClose,
  onSaved,
}: EditMeetAndGreetDetailsDialogProps) {
  const [form, setForm] = useState<FormState>(() => fromBooking(booking));
  const [errors, setErrors] = useState<Record<string, string>>({});
  const mutation = useUpdateMeetAndGreetDetails();

  useEffect(() => {
    if (open) {
      setForm(fromBooking(booking));
      setErrors({});
    }
  }, [open, booking]);

  const update = (partial: Partial<FormState>) => setForm((f) => ({ ...f, ...partial }));
  const disabled = mutation.isPending;

  const emailChanged = useMemo(
    () => !!booking && form.contact1Email.trim().toLowerCase() !== (booking.contact1Email ?? '').toLowerCase(),
    [booking, form.contact1Email]
  );
  const newEmail = form.contact1Email.trim().toLowerCase();
  // The server's rule, never re-derived here (#249 D4).
  const willSendNewLink = emailChanged && Boolean(booking?.canResendRegistration);
  const willRevokeLink = emailChanged && !willSendNewLink && Boolean(booking?.hasRegistrationToken);
  const willSendVerification = emailChanged && booking?.status === 'PENDING_VERIFICATION';

  const validate = (): Record<string, string> => {
    const e: Record<string, string> = {};
    if (!form.studentFirstName.trim()) e.studentFirstName = 'Required';
    if (!form.studentLastName.trim()) e.studentLastName = 'Required';
    if (!form.contact1Name.trim()) e.contact1Name = 'Required';
    if (!form.contact1Email.trim()) e.contact1Email = 'Required';
    else if (!EMAIL_RE.test(form.contact1Email.trim())) e.contact1Email = 'Invalid email';
    if (!form.contact1Phone.trim()) e.contact1Phone = 'Required';
    if (!form.contact1Relationship.trim()) e.contact1Relationship = 'Required';
    if (form.contact2Email.trim() && !EMAIL_RE.test(form.contact2Email.trim())) e.contact2Email = 'Invalid email';
    if (!form.emergencyName.trim()) e.emergencyName = 'Required';
    if (!form.emergencyPhone.trim()) e.emergencyPhone = 'Required';
    if (!form.emergencyRelationship.trim()) e.emergencyRelationship = 'Required';
    return e;
  };

  const handleSubmit = () => {
    if (!booking) return;
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    const patch = buildDetailsPatch(booking, form);
    if (Object.keys(patch).length === 0) {
      onClose();
      return;
    }
    mutation.mutate(
      { id: booking.id, data: patch },
      {
        onSuccess: (updated) => {
          onSaved?.(updated);
          onClose();
        },
      }
    );
  };

  const field = (
    key: keyof FormState,
    label: string,
    extra: { required?: boolean; type?: string } = {}
  ) => (
    <TextField
      fullWidth
      size="small"
      label={label}
      required={extra.required}
      type={extra.type}
      value={form[key]}
      onChange={(e) => update({ [key]: e.target.value } as Partial<FormState>)}
      error={!!errors[key]}
      helperText={errors[key]}
      disabled={disabled}
      inputProps={{ 'aria-label': label }}
    />
  );

  const relationship = (key: 'contact1Relationship' | 'contact2Relationship' | 'emergencyRelationship', required: boolean) => (
    <Autocomplete
      freeSolo
      options={RELATIONSHIPS}
      value={form[key]}
      onChange={(_, v) => update({ [key]: v ?? '' } as Partial<FormState>)}
      onInputChange={(_, v) => update({ [key]: v } as Partial<FormState>)}
      disabled={disabled}
      renderInput={(params) => (
        <TextField
          {...params}
          size="small"
          label="Relationship"
          required={required}
          error={!!errors[key]}
          helperText={errors[key]}
        />
      )}
    />
  );

  return (
    <Dialog open={open} onClose={disabled ? undefined : onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Edit Contact Details</DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 0.5 }}>
          {willSendNewLink && (
            <Alert severity="info" data-testid="new-link-notice">
              {booking?.hasRegistrationToken
                ? 'Saving will cancel the registration link sent to the old address and '
                : 'Saving will '}
              <strong>send a new registration link to {newEmail}</strong>.
            </Alert>
          )}
          {willRevokeLink && (
            <Alert severity="warning" data-testid="revoke-warning">
              Changing the primary email will <strong>revoke the registration link already sent</strong> to
              the old address.
            </Alert>
          )}
          {willSendVerification && (
            <Alert severity="info" data-testid="verification-hint">
              This booking is still awaiting email verification. Saving will{' '}
              <strong>send a new verification email to {newEmail}</strong>.
            </Alert>
          )}

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Student</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{field('studentFirstName', 'First Name', { required: true })}</Grid>
              <Grid item xs={12} sm={6}>{field('studentLastName', 'Last Name', { required: true })}</Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Primary Contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{field('contact1Name', 'Name', { required: true })}</Grid>
              <Grid item xs={12} sm={6}>{relationship('contact1Relationship', true)}</Grid>
              <Grid item xs={12} sm={6}>{field('contact1Email', 'Email', { required: true, type: 'email' })}</Grid>
              <Grid item xs={12} sm={6}>{field('contact1Phone', 'Phone', { required: true })}</Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>
              Secondary Contact{' '}
              <Typography component="span" variant="caption" color="text.secondary">(optional)</Typography>
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{field('contact2Name', 'Secondary Name')}</Grid>
              <Grid item xs={12} sm={6}>{relationship('contact2Relationship', false)}</Grid>
              <Grid item xs={12} sm={6}>{field('contact2Email', 'Secondary Email', { type: 'email' })}</Grid>
              <Grid item xs={12} sm={6}>{field('contact2Phone', 'Secondary Phone')}</Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5 }}>Emergency Contact</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>{field('emergencyName', 'Emergency Name', { required: true })}</Grid>
              <Grid item xs={12} sm={6}>{relationship('emergencyRelationship', true)}</Grid>
              <Grid item xs={12} sm={6}>{field('emergencyPhone', 'Emergency Phone', { required: true })}</Grid>
            </Grid>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={disabled}>Cancel</Button>
        <Button variant="contained" onClick={handleSubmit} disabled={disabled || !booking}>
          {disabled ? 'Saving…' : 'Save Changes'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
